/**
 * GroupPageHeader — shared header for HrSkillsBridger pages: the standard
 * PageHeader plus the module navigation tabs.
 *
 * Used by: GroupSkillsProficiency, SkillsMap
 *
 * Props
 * ─────────────────────────────────────────────────────────────────────────────
 * activeTab                Active tab key (use PAIC_TAB_KEYS)
 * onBack                   Back button click handler
 * onTabChange              Called with the tab key when a tab is clicked
 * skillsBridgerDropdownItems  Menu items shown inside the Skills Bridger dropdown
 * rightDropdownLabel       Label on the right-side dropdown button
 * rightDropdownItems       Menu items shown inside the right-side dropdown
 * pageTitle / pageSubtitle  Built by groupPageHeader.utils (Group: <role> / Employee: <name>)
 */

import React, { useMemo } from "react";
import { Flex, Dropdown } from "antd";
import PageHeader from "Components/PageHeader";
import { PAIC_TAB_KEYS } from "constants/peoplePlusAI.constants";
import { HR_TAB_LABELS } from "./GroupPageHeader.constants";
import {
  PageHeaderContainerStyled,
  NavTabsStyled,
  RightDropdownTriggerStyled,
  NavTabDropdownIconStyled,
} from "./GroupPageHeader.styles";

// Static tab definitions — Skills Bridger label is built dynamically in useMemo.
const BASE_TABS = [
  { key: PAIC_TAB_KEYS.JOBS_LIBRARY,      label: HR_TAB_LABELS.JOBS_LIBRARY },
  { key: PAIC_TAB_KEYS.SKILLS_BRIDGER,    label: null },
  { key: PAIC_TAB_KEYS.LEARNING_CATALYST, label: HR_TAB_LABELS.LEARNING_CATALYST },
  { key: PAIC_TAB_KEYS.TALENT_MOBILITY,   label: HR_TAB_LABELS.TALENT_MOBILITY },
];

const GroupPageHeader = ({
  activeTab,
  onBack,
  onTabChange,
  skillsBridgerDropdownItems,
  rightDropdownLabel,
  rightDropdownItems,
  pageTitle,
  pageSubtitle,
}) => {
  // Skills Bridger tab gets a Dropdown label; everything else is plain text.
  const tabItems = useMemo(
    () =>
      BASE_TABS.map((tab) => {
        if (tab.key !== PAIC_TAB_KEYS.SKILLS_BRIDGER) return tab;

        return {
          key: tab.key,
          label: (
            <Dropdown
              menu={{ items: skillsBridgerDropdownItems }}
              trigger={["click"]}
            >
              {/* stopPropagation prevents Tabs from switching to this key on click */}
              <Flex
                align="center"
                gap={6}
                onClick={(e) => e.stopPropagation()}
              >
                <span>Skills Bridger</span>
                <NavTabDropdownIconStyled />
              </Flex>
            </Dropdown>
          ),
        };
      }),
    [skillsBridgerDropdownItems]
  );

  // Right-side extra content rendered inside the Tabs bar on the right edge.
  const rightExtra = useMemo(() => {
    if (!rightDropdownItems?.length) return null;

    return (
      <Dropdown
        menu={{ items: rightDropdownItems }}
        trigger={["click"]}
      >
        <RightDropdownTriggerStyled>
          {rightDropdownLabel}
          <NavTabDropdownIconStyled />
        </RightDropdownTriggerStyled>
      </Dropdown>
    );
  }, [rightDropdownLabel, rightDropdownItems]);

  return (
    <>
      {/* Standard header: back button + title + subtitle */}
      <PageHeader title={pageTitle} subtitle={pageSubtitle} onBack={onBack} />

      <PageHeaderContainerStyled>
      {/* AntD Tabs replaces the custom button tab row */}
      <NavTabsStyled
        activeKey={activeTab}
        onChange={onTabChange}
        items={tabItems}
        animated={false}
        tabBarExtraContent={{ right: rightExtra }}
      />

      </PageHeaderContainerStyled>
    </>
  );
};

export default GroupPageHeader;
